import { Button } from '@/components/ui/button';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';

export function KeywordFilters() {
  const { profile, loadingProfile } = useProfile();
  const { keywords, loading } = useKeywords();
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const isAdmin = profile?.role === AdminRole;

  const handleKeywordClick = (keywordId: string, keywordName: string) => {
    setSelectedKeywords([keywordId]); // only allow one keyword selected

    // Navigate to search results with this keyword
    navigate(`/search?query=${encodeURIComponent(keywordName)}`);
  };

  if (loading) {
    return <div className="flex flex-wrap gap-2">Loading keywords...</div>;
  }

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const handleKeywordVisibilityToggle = (keywordId: string) => {
    // In edit mode, clicking a keyword toggles its visibility
    if (isEditing) {
      // This is where you would implement the logic to hide/show keywords
      // For now, we'll just toggle the selection for demonstration
      if (selectedKeywords.includes(keywordId)) {
        setSelectedKeywords(selectedKeywords.filter((id) => id !== keywordId));
      } else {
        setSelectedKeywords([...selectedKeywords, keywordId]);
      }
    }
  };

  return (
    <div className="relative">
      {isAdmin && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute -top-8 right-0 h-8 w-8"
          onClick={toggleEditMode}
        >
          <Pencil className="h-4 w-4" />
          <span className="sr-only">Edit Keywords</span>
        </Button>
      )}
      <div className="flex gap-2 overflow-x-auto flex-nowrap pb-2 scrollbar-hide sm:grid sm:grid-rows-2 sm:grid-flow-col sm:auto-cols-max sm:pb-2 lg:flex lg:flex-wrap lg:pb-0 lg:overflow-visible">
        {keywords.map((keyword) => (
          <Button
            key={keyword.id}
            variant="secondary"
            className={`rounded-full flex-shrink-0
              ${selectedKeywords.includes(keyword.id) ? 'bg-green-700' : 'bg-green-500'}
              ${isEditing ? 'border-2 border-dashed border-white' : ''}
              text-white hover:bg-green-600 relative hhu-outline`}
            onClick={() =>
              isEditing
                ? handleKeywordVisibilityToggle(keyword.id)
                : handleKeywordClick(keyword.id, keyword.name)
            }
          >
            {keyword.name}
            {isEditing && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-xs text-green-500">
                {selectedKeywords.includes(keyword.id) ? '✓' : '×'}
              </span>
            )}
          </Button>
        ))}
      </div>
      {isEditing && (
        <div className="mt-4">
          <Button
            variant="outline"
            className="mr-2"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => {
              // Here you would save the changes to the database
              // For now, we'll just exit edit mode
              setIsEditing(false);
            }}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
